import isArray from 'lodash/isArray';
import ObjectId from 'bson-objectid';

import { renderCurrency } from '../../components/SystemCountry';

import { createChangeLogSection } from '../../components/FieldChangeLog';

import tests from '../../helpers/tests';

import Types from '../../modules/types';

const {
  viewerIsNotAdmin,
  activityIsNotCreate,
} = tests;

const createNotSupportedTest = (method, suffix) => ([
  'condition.testFieldValue',
  `methods.${method}.${suffix}`,
  '!==',
  true,
]);

export const FIELDS = [
  ['FieldText', {
    name: 'name',
    rules: [['validation.isRequired']],
  }],
  // ['FieldSwitch', {
  //   name: 'active',
  //   initialValue: true,
  //   rules: [['validation.isRequired']],
  // }],
  // ['Divider', { name: 'dividerCountries' }],
  ['FieldConnectionSelect', {
    name: 'store',
    label: 'Store',
    url: '/stores',
    placeholder: 'Leave empty for Packman Shop global pricing',
    allowClear: true,
    hidden: [viewerIsNotAdmin],
    virtual: [viewerIsNotAdmin, activityIsNotCreate],
    disabled: [activityIsNotCreate],
    searchPaths: ['name'],
    handleChange: (value, props) => {
      props.form.setFieldsValue(Types.SHIPPING_PRICING_METHOD.reduce(
        (agr, pricingMethod) => {
          agr[`methods.${pricingMethod}.shippingCompany`] = null;
          return agr;
        },
        {},
      ));
    },
  }],
  ['FieldSelect', {
    name: 'countries',
    mode: 'multiple',
    optionFilterProp: 'children',
    choices: Types.COUNTRIES.map(({ name, iso3a2 }) => ({
      label: name,
      value: iso3a2,
    })),
    rules: [['validation.isRequired']],
  }],
  ...[
    ...Types.SHIPPING_PRICING_METHOD,
  ].reduce(
    (fields, pricingMethod) => {
      const isMethodNotSupported = createNotSupportedTest(
        pricingMethod,
        'supported',
      );
      const isWeightsExtraNotSupported = createNotSupportedTest(
        pricingMethod,
        'weightsExtra.supported',
      );
      const isPriceExtraNotSupported = createNotSupportedTest(
        pricingMethod,
        'priceExtra.supported',
      );
      fields.push(
        ...[
          ['FieldTitle', {
            name: `divider-${pricingMethod}`,
            title: Types.SHIPPING_PRICING_METHOD_LABELS_MAP[pricingMethod],
          }],
          ['FieldRadio', {
            name: `methods.${pricingMethod}.supported`,
            label: null,
            choices: [
              { value: true, label: 'Enabled' },
              { value: false, label: 'Disabled' },
            ],
            block: true,
            initialValue: false,
          }],
          ['FieldConnectionSelect', {
            name: `methods.${pricingMethod}.shippingCompany`,
            label: null,
            prefix: 'Default Shipping Company:',
            url: '/shippingcompanies',
            placeholder: `Set default for ${
              Types.SHIPPING_PRICING_METHOD_LABELS_MAP[pricingMethod]
            }`,
            allowClear: true,
            searchPaths: ['name'],
            getExtraQueryConditions: (props) => {
              const storeId = props.form?.getFieldValue('store');
              if (storeId) {
                return [{ store: storeId }];
              }
              return [{ store: { EQ: null } }];
            },
            skip: [isMethodNotSupported],
          }],
          ['FieldTable', {
            name: `methods.${pricingMethod}.weights`,
            label: 'Price per maximum weight',
            skip: [isMethodNotSupported],
            table: {
              showHeader: true,
              scroll: { x: '370px' },
            },
            controlsEnabled: true,
            controlsGetNewItem: () => ({
              _id: `${ObjectId()}`,
            }),
            prepareValueForInput: (value, props) => (
                !isArray(value) || !value.length
              ? [props.controlsGetNewItem()]
              : value
            ),
            columns: [{
              key: 'weight',
              title: 'Maximum weight',
              fields: [{
                name: 'weight',
                props: {
                  size: 'small',
                  align: 'right',
                  placeholder: 'Under this weight',
                  addonBefore: 'grams',
                },
              }],
            }, {
              ket: 'price',
              title: 'Amount to charge',
              fields: [{
                name: 'price',
                props: {
                  size: 'small',
                  align: 'right',
                  placeholder: 'Amount to charge',
                  addonBefore: renderCurrency(),
                },
              }],
            }],
          }],
          ['FieldSwitch', {
            name: `methods.${pricingMethod}.weightsExtra.supported`,
            label: 'Allow extra weight',
            initialValue: false,
            skip: [
              isMethodNotSupported,
            ],
          }],
          ['FieldText', {
            col: 12,
            name: `methods.${pricingMethod}.weightsExtra.unit`,
            label: 'Extra weight unit',
            addonAfter: 'grams',
            skip: [
              isMethodNotSupported,
              isWeightsExtraNotSupported,
            ],
          }],
          ['FieldText', {
            col: 12,
            name: `methods.${pricingMethod}.weightsExtra.price`,
            label: 'Extra weight price per unit',
            addonAfter: renderCurrency(),
            skip: [
              isMethodNotSupported,
              isWeightsExtraNotSupported,
            ],
          }],
          ['FieldSwitch', {
            name: `methods.${pricingMethod}.priceExtra.supported`,
            label: 'Add extra price',
            initialValue: false,
            skip: [
              isMethodNotSupported,
            ],
          }],
          ['FieldTable', {
            name: `methods.${pricingMethod}.priceExtra.levels`,
            label: 'Extra price levels',
            skip: [
              isMethodNotSupported,
              isPriceExtraNotSupported,
            ],
            table: {
              showHeader: true,
              scroll: { x: '370px' },
            },
            controlsEnabled: true,
            controlsGetNewItem: () => ({
              _id: `${ObjectId()}`,
            }),
            prepareValueForInput: (value, props) => (
                !isArray(value) || !value.length
              ? [props.controlsGetNewItem()]
              : value
            ),
            columns: [{
              key: 'threshold',
              title: 'Price threshold',
              fields: [{
                name: 'threshold',
                props: {
                  size: 'small',
                  align: 'right',
                  placeholder: 'Price threshold',
                  addonBefore: renderCurrency(),
                },
              }],
            }, {
              key: 'price',
              title: 'Amount to add',
              fields: [{
                name: 'price',
                props: {
                  size: 'small',
                  align: 'right',
                  placeholder: 'Amount to charge',
                  addonBefore: renderCurrency(),
                },
              }],
            }],
          }],
          ['FieldProductAttributes', {
            name: `methods.${pricingMethod}.attributes`,
            label: 'Shipping disclaimers support and their prices',
            skip: [isMethodNotSupported],
          }],
          ['FieldPaymentMethods', {
            label: 'Payment methods support and their prices',
            name: `methods.${pricingMethod}.payment`,
            help: null,
            skip: [isMethodNotSupported],
          }],
        ].map(([fieldType, fieldConfig]) => ([fieldType, {
          ...fieldConfig,
          skip: [
            // isMethodSkipped,
            ...(fieldConfig.skip || []),
          ],
        }])),
      );
      return fields;
    },
    [],
  ),
  ...createChangeLogSection({
    changeLogConfig: {
      getWhereConditions: props => ({
        store: { EQ: null },
        'target.type': 'Pricing',
        'target.subject': props.data._id,
      }),
      skipColumns: {
        store: true,
        realm: true,
        target: true,
      },
    },
  }),
];

export const ADMIN = FIELDS;
